import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import {
  availableCompanyFocusAreas,
  companyPortalCandidateCardTemplate,
  companyPortalIdentityTemplate,
  filterCompanyProfiles,
  type CompanyPortalLabels,
} from '@vivaporta/company-portal-ui';
import { html } from 'lit';
import type { LandingCompanyPlatformHeroContent } from '../../shared/content/landingContent.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';
import { openCompanyContactDialog } from './navigation.js';

interface CompanyMatchingDemoProps {
  hero: LandingCompanyPlatformHeroContent;
  editor?: LandingEditorRenderOptions;
}

export const companyMatchingDemo = stateful(({ hero, editor }: CompanyMatchingDemoProps) => {
  const [search, updateSearch] = useState('');
  const [focus, updateFocus] = useState('');
  const setSearch = (value: string) => { if (editor?.mode !== 'editor') updateSearch(value); };
  const setFocus = (value: string) => { if (editor?.mode !== 'editor') updateFocus(value); };
  const [interestedIds, setInterestedIds] = useState<Set<string>>(new Set());
  const [withdrawCandidateId, setWithdrawCandidateId] = useState<string | null>(null);
  const [documentNotice, setDocumentNotice] = useState('');
  const matching = hero.matching;
  const demoCandidates = matching.candidates.map((candidate, index) => ({
    ...candidate,
    id: `demo-${index}`,
    age: Number.parseInt(candidate.age, 10) || undefined,
    editorIndex: index,
  }));
  const labels: CompanyPortalLabels = {
    ...matching.labels,
    photoAlt: name => matching.labels.photoAlt.replace('{name}', name),
    ageValue: age => matching.labels.ageValue.replace('{age}', String(age)),
  };
  const text = (path: string, value: string) => {
    const id = `companyPlatformHero.matching.${path}`;
    return html`<span
      class=${editorNodeClass(editor, id)}
      style=${editorNodeStyle(editor, id)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, id, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, id, event)}
      >${value}</span
    >`;
  };
  const image = (path: string, url: string, alt: string) => {
    const id = `companyPlatformHero.matching.${path}`;
    return html`<img
      src=${url}
      alt=${alt}
      loading="lazy"
      decoding="async"
      class=${editorNodeClass(editor, id)}
      style=${editorNodeStyle(editor, id)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, id, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, id, event)}
    />`;
  };
  const focusAreas = availableCompanyFocusAreas(demoCandidates);
  const candidates = filterCompanyProfiles(demoCandidates, search, focus);
  const toggleInterest = (candidateId: string) => {
    if (editor?.mode === 'editor') return;
    if (interestedIds.has(candidateId)) {
      setWithdrawCandidateId(candidateId);
      return;
    }
    setInterestedIds(new Set([...interestedIds, candidateId]));
  };
  const confirmWithdraw = (candidateId: string) => {
    if (editor?.mode === 'editor') return;
    const next = new Set(interestedIds);
    next.delete(candidateId);
    setInterestedIds(next);
    setWithdrawCandidateId(null);
  };

  const renderCandidate = (candidate: (typeof demoCandidates)[number]) => {
    const id = `companyPlatformHero.matching.candidates.${candidate.editorIndex}`;
    return html`<div
      class=${editorNodeClass(editor, id, 'vp-company-platform-profile')}
      style=${editorNodeStyle(editor, id)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, id, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, id, event)}
    >
      ${companyPortalCandidateCardTemplate({
        candidate,
        labels,
        renderField: (path, value) =>
          text(path.startsWith('labels.') ? path : `candidates.${candidate.editorIndex}.${path}`, value),
        renderImage: (url, alt) => image(`candidates.${candidate.editorIndex}.profileImageUrl`, url, alt),
        state: {
          interested: interestedIds.has(candidate.id),
          showWithdrawConfirmation: withdrawCandidateId === candidate.id,
        },
        actions: {
          onToggleInterest: () => toggleInterest(candidate.id),
          onKeepInterest: () => setWithdrawCandidateId(null),
          onConfirmWithdraw: () => confirmWithdraw(candidate.id),
          onDocument: () => { if (editor?.mode !== 'editor') setDocumentNotice(labels.documentUnavailable); },
        },
        focusAreaLabel: area => area,
        experienceYearsLabel: value => matching.experienceValue.replace('{value}', value),
        germanLevelLabel: value => value.toLocaleUpperCase(),
      })}
    </div>`;
  };

  return html`
    <div
      id="matching-demo"
      class=${editorNodeClass(editor, 'companyPlatformHero.demo', 'vp-company-platform-demo')}
      style=${editorNodeStyle(editor, 'companyPlatformHero.demo')}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'companyPlatformHero.demo', event)}
      @click=${(event: MouseEvent) =>
        editor?.mode === 'editor' && editorNodeClick(editor, 'companyPlatformHero.demo', event)}
    >
      <button
        type="button"
        class="vp-company-platform-preview-open"
        aria-label=${hero.primaryAction.label}
        @click=${(event: MouseEvent) =>
          (event.currentTarget as HTMLElement)
            .closest('vp-company-platform-hero')
            ?.dispatchEvent(new CustomEvent('open-matching-demo', { detail: { instant: event.detail === 0 } }))}
      ></button>
      <div class="vp-company-platform-window" aria-label=${matching.windowTitle}
        @click=${(event: MouseEvent) => { if (editor?.mode === 'editor') event.preventDefault(); }}>

        <div class="vp-company-platform-windowbar">
          <span class="vp-company-platform-windowdots" aria-hidden="true"><i></i><i></i><i></i></span>
          <strong>${text('windowTitle', matching.windowTitle)}</strong>
          <span class="vp-company-platform-demo-badge">${text('badge', matching.badge)}</span>
        </div>
        <div class="vp-company-platform-portal">
          <header class="vp-company-platform-portal-header">
            ${companyPortalIdentityTemplate({ ...matching, renderField: text, renderImage: image })}
          </header>
          <div class="vp-company-platform-portal-main">
            <div class="vp-company-platform-demo-heading">
              <span><strong>${candidates.length}</strong> ${text('resultLabel', matching.resultLabel)}</span>
            </div>
            <section class="vp-company-discovery" aria-label=${matching.searchLabel}>
              <label class="vp-company-search"
                ><span>${text('searchLabel', matching.searchLabel)}</span>
                <div>
                  <span class="icon-[material-symbols--search-rounded]" aria-hidden="true"></span
                  ><input
                    .value=${search}
                    .readOnly=${editor?.mode === 'editor'}
                    placeholder=${matching.searchPlaceholder}
                    @input=${(event: Event) => setSearch((event.currentTarget as HTMLInputElement).value)}
                  /></div
              ></label>
              <div class="vp-company-focus-filter">
                <div class="vp-company-filter-heading">
                  <span>${text('filterLabel', matching.filterLabel)}</span>${focus || search
                    ? html`<button type="button" @click=${() => { setFocus(''); setSearch(''); }}>
                        ${text('resetLabel', matching.resetLabel)}
                      </button>`
                    : ''}
                </div>
                <div class="vp-company-filter-options">
                  <button
                    type="button"
                    aria-pressed=${!focus}
                    class=${focus ? '' : 'is-active'}
                    @click=${() => setFocus('')}
                  >
                    ${text('allLabel', matching.allLabel)}</button
                  >${focusAreas.map(
                    area =>
                      html`<button
                        type="button"
                        aria-pressed=${focus === area}
                        class=${focus === area ? 'is-active' : ''}
                        @click=${() => setFocus(area)}
                      >
                        ${area}
                      </button>`,
                  )}
                </div>
              </div>
            </section>
            ${documentNotice
              ? html`<p class="vp-company-platform-document-notice" role="status">
                  <span class="icon-[material-symbols--lock-outline]" aria-hidden="true"></span
                  >${documentNotice}
                </p>`
              : ''}
            <div class="vp-company-platform-candidate-scroll">
              ${candidates.length
                ? html`<div class="vp-company-candidate-grid">
                    ${candidates.map(candidate => renderCandidate(candidate))}
                  </div>`
                : html`<div class="vp-company-platform-empty">
                    <span class="icon-[material-symbols--person-search-rounded]" aria-hidden="true"></span
                    ><strong>${text('emptyTitle', matching.emptyTitle)}</strong>
                    <p>${text('emptyText', matching.emptyText)}</p>
                    <button type="button" class="vp-action vp-action-secondary" @click=${() => { setFocus(''); setSearch(''); }}>${matching.resetLabel}</button>
                  </div>`}
            </div>
            <footer class="vp-company-platform-demo-cta">
              <div>
                ${editorSemanticText({
                  editor,
                  elementId: 'companyPlatformHero.demoCtaTitle',
                  fallbackTag: 'h3',
                  content: hero.demoCtaTitle,
                })}
                <p>${text('ctaText', matching.ctaText)}</p>
              </div>
              <button
                class=${editorNodeClass(editor, 'companyPlatformHero.demoCtaLabel', 'vp-action vp-action-primary')}
                style=${editorNodeStyle(editor, 'companyPlatformHero.demoCtaLabel')}
                type="button"
                @pointerdown=${(event: PointerEvent) =>
                  editorNodePointerDown(editor, 'companyPlatformHero.demoCtaLabel', event)}
                @click=${(event: MouseEvent) => {
                  if (editor?.mode === 'editor') {
                    editorNodeClick(editor, 'companyPlatformHero.demoCtaLabel', event);
                    return;
                  }
                  openCompanyContactDialog((event.currentTarget as HTMLElement).closest('.vp-page'));
                }}
              >
                ${hero.demoCtaLabel}<span
                  class="icon-[material-symbols--arrow-forward-rounded]"
                  aria-hidden="true"
                ></span>
              </button>
            </footer>
          </div>
        </div>
      </div>
    </div>
  `;
});
